import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent, type KeyboardEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertCircle, AlertTriangle, FileSpreadsheet, Inbox, Lock, Upload, X } from 'lucide-react';

import { EmptyState, ErrorState, Skeleton } from '@/components/chassis/States';
import { Pager } from '@/components/chassis/Pager';
import { useBatchesPage, useBatchDetail, usePreviewBatch, useUploadBatch } from '@/features/batches/useBatches';
import { toast } from '@/stores/toast.store';
import { cn } from '@/lib/utils';
import { useT, useLang } from '@/i18n';
import type { BatchListItem, BatchPreviewResponse, UploadMetadata } from '@/types/api';

/**
 * F5-T6c · Admin 12 — «Lotes de pacientes» (contrato 2026-08-28).
 *
 * FUSIÓN subir+listar: el historial ES la pantalla, «Subir un lote» es su
 * único botón primario (modal de 2 pasos dentro de la tarjeta), y el detalle
 * se abre INLINE debajo de la lista (tercera pantalla MUERTA). Las rutas
 * viejas /admin/batches/upload y /admin/batches/:id redirigen acá (no se
 * puede tocar AdminShell en esta tanda).
 *
 * EL FLUJO DE SUBIDA NO CAMBIA (regla de la casa — transacción síncrona con
 * datos reales en prod): mismos hooks usePreviewBatch → useUploadBatch,
 * mismas validaciones de tipo y tamaño, mismo bloqueo por hash duplicado.
 * Solo cambia el vestido. Los metadatos opcionales (programa, fecha de
 * origen, notas) SE CONSERVAN en el paso 1: son parte del payload del upload
 * (quitarlos cambiaría el flujo, no el vestido).
 *
 * MUERE por contrato: las dos pantallas sueltas (fusionadas) · el borrado de
 * lotes (nunca existió acá) · la barra de progreso animada (nunca existió).
 * Por la regla whitelist del contrato mueren también el «Export CSV» del
 * listado y el botón «Refresh» (controles no dibujados; declarados en el
 * reporte para que no mueran en silencio).
 *
 * SE CONSERVA demotado (precedente E-23, colapsado dentro del detalle /
 * preview): breakdown de estados, reparto por agente, notas y hash SHA-256
 * del lote; avisos del parser y demografía del preview.
 *
 * NECESITA-SERVER:
 * - «Creando… X de Y» (lote en proceso consultable): el upload es una TX
 *   síncrona, no hay job async que consultar — ninguna fila puede estar «en
 *   proceso» en el historial.
 * - «{n} lotes desde {mes}» del pie: la página no trae la fecha del primer
 *   lote — se muestra «Página X de Y · N lotes».
 * - «Bajar el reporte del lote» (CSV por fila con razón, auditado): no
 *   existe el endpoint → botón deshabilitado con diagnóstico.
 * - Filas de menores en la lista de observación del preview: el server manda
 *   el CONTEO de menores (stats.minorsCount) pero no las filas una a una.
 */

const PAGE_SIZE = 25;
const MAX_BYTES = 10 * 1024 * 1024;

export function BatchesListPage(): JSX.Element {
  const t = useT();
  const lang = useLang();
  const locale = lang === 'es' ? 'es' : 'en';
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Math.max(1, parseInt(searchParams.get('page') ?? '1', 10) || 1);
  const offset = (page - 1) * PAGE_SIZE;
  const { data, isLoading, error, isFetching, refetch } = useBatchesPage(PAGE_SIZE, offset);

  const [selectedId, setSelectedId] = useState<string | null>(searchParams.get('open'));
  const [uploadOpen, setUploadOpen] = useState(searchParams.get('upload') === '1');

  // Los params `upload`/`open` vienen de los redirects de las rutas viejas;
  // una vez consumidos se limpian para que el back del browser no re-abra.
  useEffect(() => {
    if (searchParams.get('upload') === '1' || searchParams.get('open')) {
      const n = new URLSearchParams(searchParams);
      n.delete('upload');
      n.delete('open');
      setSearchParams(n, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const goTo = (p0: number): void => {
    const p = Math.min(Math.max(1, p0 + 1), totalPages);
    setSearchParams({ page: String(p) });
    setSelectedId(null);
  };

  const fmtDate = (iso: string): string => {
    const d = new Date(iso);
    return `${d.toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' })} ${d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}`;
  };
  const fmtNum = (n: number): string => n.toLocaleString(locale);

  const toggleDetail = (id: string): void => {
    setSelectedId((cur) => (cur === id ? null : id));
  };

  const footerLabel = t('f5t6c.batches.footer', { page, pages: totalPages, n: total });

  return (
    <div className="max-w-[1440px]">
      <header className="max-w-[760px] mb-6">
        <h1 className="font-head text-[32px] font-extrabold tracking-[-0.025em] leading-tight text-nav-navy mb-2">
          {t('f5t6c.batches.title')}
        </h1>
        <p className="font-body text-[15px] leading-relaxed text-nav-navy/90 m-0">
          {t('f5t6c.batches.subtitle')}
        </p>
      </header>

      {/* Tarjeta principal — ancla del modal (position:relative). */}
      <section className="relative bg-nav-soft border-[1.5px] border-nav-card rounded-[14px] px-8 pt-7 pb-[34px] animate-fade-up-slow motion-reduce:animate-none">
        <div className="flex items-center justify-between gap-4 flex-wrap mb-[22px]">
          <p className="font-head text-[10.5px] font-bold tracking-[.14em] uppercase text-nav-teal m-0">
            {t('f5t6c.batches.kicker', { n: total })}
          </p>
          <button
            type="button"
            onClick={() => setUploadOpen(true)}
            disabled={uploadOpen}
            className="inline-flex items-center gap-2 min-h-[44px] px-[18px] bg-nav-navy text-white border-[1.5px] border-nav-navy rounded-lg font-head text-[13.5px] font-bold hover:bg-nav-navyHover disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2"
          >
            <Upload className="h-4 w-4" aria-hidden />
            {t('f5t6c.batches.upload')}
          </button>
        </div>

        {error ? (
          <div className="bg-white border-[1.5px] border-nav-card rounded-[12px]">
            <ErrorState
              icon={<AlertCircle className="h-5 w-5" aria-hidden />}
              title={t('f5t6c.batches.error.title')}
              body={t('f5t6c.batches.error.body')}
              action={{ label: t('f5t6c.batches.error.retry'), onClick: () => refetch() }}
            />
          </div>
        ) : isLoading ? (
          <div className="bg-white border-[1.5px] border-nav-card rounded-[12px] p-4 space-y-2" aria-busy="true">
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-[64px] w-full" />
            ))}
          </div>
        ) : data && data.items.length === 0 ? (
          <div className="bg-white border-[1.5px] border-nav-card rounded-[12px]">
            <EmptyState
              icon={<Inbox className="h-5 w-5" aria-hidden />}
              title={t('f5t6c.batches.empty.title')}
              body={t('f5t6c.batches.empty.body')}
              action={{ label: t('f5t6c.batches.upload'), onClick: () => setUploadOpen(true) }}
            />
          </div>
        ) : (
          <>
            <div className="bg-white border-[1.5px] border-nav-card rounded-[12px] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse font-body">
                  <thead>
                    <tr className="bg-[#fafbfc] border-b border-nav-hair text-left">
                      {[
                        t('f5t6c.batches.col.file'),
                        t('f5t6c.batches.col.uploaded'),
                        t('f5t6c.batches.col.rows'),
                        t('f5t6c.batches.col.created'),
                        t('f5t6c.batches.col.ignored'),
                        t('f5t6c.batches.col.rejected'),
                        t('f5t6c.batches.col.status'),
                      ].map((h, i) => (
                        <th
                          key={h}
                          className={cn(
                            'h-[38px] px-[18px] font-head text-[10.5px] font-bold tracking-[.1em] uppercase text-nav-slate',
                            i === 0 && 'pl-6',
                          )}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data?.items.map((b, i) => (
                      <BatchRow
                        key={b.id}
                        b={b}
                        index={i}
                        selected={selectedId === b.id}
                        onToggle={() => toggleDetail(b.id)}
                        fmtDate={fmtDate}
                        fmtNum={fmtNum}
                        t={t}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between gap-4 flex-wrap px-6 py-4 border-t border-nav-hair">
                <p className="font-body text-[13px] text-nav-slate tabular-nums m-0">{footerLabel}</p>
                <p className="font-body text-[13px] text-nav-slate m-0">{t('f5t6c.batches.footerHint')}</p>
              </div>
            </div>
            {totalPages > 1 && (
              <Pager
                page={page - 1}
                pages={totalPages}
                onPage={goTo}
                busy={isFetching}
                prevLabel={t('f5t6c.pager.prev')}
                nextLabel={t('f5t6c.pager.next')}
                pageLabel={footerLabel}
              />
            )}
          </>
        )}

        {/* DETALLE DEL LOTE — inline debajo de la lista, no tercera pantalla. */}
        {selectedId && data && (
          <BatchInlineDetail
            item={data.items.find((b) => b.id === selectedId) ?? null}
            id={selectedId}
            onClose={() => setSelectedId(null)}
            fmtDate={fmtDate}
            fmtNum={fmtNum}
            t={t}
          />
        )}

        {/* MODAL DE SUBIDA — dos pasos, el primero no escribe nada. */}
        {uploadOpen && (
          <UploadModal
            onClose={() => setUploadOpen(false)}
            onUploaded={() => {
              setUploadOpen(false);
              refetch();
            }}
            fmtNum={fmtNum}
            t={t}
          />
        )}
      </section>
    </div>
  );
}

function BatchRow({
  b,
  index,
  selected,
  onToggle,
  fmtDate,
  fmtNum,
  t,
}: {
  b: BatchListItem;
  index: number;
  selected: boolean;
  onToggle: () => void;
  fmtDate: (iso: string) => string;
  fmtNum: (n: number) => string;
  t: ReturnType<typeof useT>;
}): JSX.Element {
  // Rechazados de la tabla = filas con error + inelegibles (el contrato dibuja
  // una sola columna «Rechazados»; el desglose vive en el detalle conservado).
  const rejected = b.skippedErrors + b.skippedIneligible;
  const onKey = (e: KeyboardEvent<HTMLTableRowElement>): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle();
    }
  };
  return (
    <tr
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={onKey}
      aria-expanded={selected}
      style={{ animationDelay: `${index * 50}ms` }}
      className={cn(
        'h-[64px] border-t border-nav-hair cursor-pointer transition-colors duration-150 animate-fade-up motion-reduce:animate-none',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-nav-teal focus-visible:-outline-offset-2',
        selected ? 'bg-nav-tealTint hover:bg-[#d3e8e9]' : 'hover:bg-[#f4fafa]',
      )}
    >
      <td className="px-[18px] pl-6">
        <span className="flex items-center gap-3 min-w-0">
          <span className="w-[34px] h-[34px] flex-none rounded-lg bg-nav-tealTint text-nav-tealDark inline-flex items-center justify-center">
            <FileSpreadsheet className="h-4 w-4" aria-hidden />
          </span>
          <span className="min-w-0">
            <span className="block font-head text-[13.5px] font-bold text-nav-navy truncate max-w-[260px]" title={b.filename}>
              {b.filename}
            </span>
            <span className="block font-body text-[12px] text-nav-slate truncate">
              {t('f5t6c.batches.by', { name: b.uploadedBy })}
            </span>
          </span>
        </span>
      </td>
      <td className="px-[18px] font-body text-[13.5px] text-nav-slate tabular-nums whitespace-nowrap">
        {fmtDate(b.uploadedAt)}
      </td>
      <td className="px-[18px] font-head text-[13.5px] font-bold text-nav-navy tabular-nums">{fmtNum(b.totalRows)}</td>
      <td className="px-[18px] font-head text-[13.5px] font-bold text-nav-navy tabular-nums">{fmtNum(b.validRows)}</td>
      <td className="px-[18px] font-body text-[13.5px] text-nav-slate tabular-nums">{fmtNum(b.skippedDuplicates)}</td>
      <td className="px-[18px] font-body text-[13.5px] text-nav-slate tabular-nums">{fmtNum(rejected)}</td>
      <td className="px-[18px]">
        {/* NECESITA-SERVER: «Creando… X de Y» — la TX es síncrona, todo lote
            persistido está procesado; el chip rojo «archivo repetido» no
            puede aparecer en el historial porque el dup se rechaza antes. */}
        <span className="inline-flex items-center rounded-full border border-nav-sage bg-[#eef4ee] text-nav-sageText px-[11px] py-1 font-head text-[11.5px] font-bold">
          {t('f5t6c.batches.status.processed')}
        </span>
      </td>
    </tr>
  );
}

function BatchInlineDetail({
  item,
  id,
  onClose,
  fmtDate,
  fmtNum,
  t,
}: {
  item: BatchListItem | null;
  id: string;
  onClose: () => void;
  fmtDate: (iso: string) => string;
  fmtNum: (n: number) => string;
  t: ReturnType<typeof useT>;
}): JSX.Element | null {
  const detailQ = useBatchDetail(id);
  const b = item ?? detailQ.data?.batch ?? null;
  if (!b) return null;
  const rejected = b.skippedErrors + b.skippedIneligible;

  return (
    <div className="mt-4 bg-white border-[1.5px] border-nav-teal rounded-[12px] px-[26px] py-6 animate-fade-up motion-reduce:animate-none">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
        <div className="min-w-0">
          <h2 className="font-head text-[18px] font-bold tracking-[-0.01em] text-nav-navy m-0 break-all">
            {b.filename}
          </h2>
          <p className="font-body text-[13px] text-nav-slate tabular-nums m-0 mt-1">
            {t('f5t6c.batches.detail.stamp', { when: fmtDate(b.uploadedAt), name: b.uploadedBy })}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center min-h-[44px] px-[14px] rounded-lg border-[1.5px] border-nav-line bg-white font-head text-[12.5px] font-bold text-nav-navy hover:bg-[#f2f5f7] hover:border-nav-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
        >
          {t('f5t6c.batches.detail.close')}
        </button>
      </div>

      <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-4 mb-5">
        <DetailStat label={t('f5t6c.batches.detail.stat.rows')} value={fmtNum(b.totalRows)} />
        <DetailStat label={t('f5t6c.batches.detail.stat.created')} value={fmtNum(b.validRows)} />
        <DetailStat label={t('f5t6c.batches.detail.stat.ignored')} value={fmtNum(b.skippedDuplicates)} muted />
        <DetailStat label={t('f5t6c.batches.detail.stat.rejected')} value={fmtNum(rejected)} amber />
      </div>

      <div className="bg-nav-tealTint border-[1.5px] border-[#c9e2e2] rounded-[12px] px-5 py-[18px] text-nav-tealDark mb-5">
        <p className="font-head text-[10.5px] font-bold tracking-[.12em] uppercase m-0 mb-2">
          {t('f5t6c.batches.dedupe.title')}
        </p>
        <p className="font-body text-[13.5px] leading-[1.6] m-0">{t('f5t6c.batches.dedupe.p1')}</p>
        <p className="font-body text-[13.5px] leading-[1.6] m-0 mt-2">{t('f5t6c.batches.dedupe.p2')}</p>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        {/* NECESITA-SERVER: reporte CSV por lote, auditado — endpoint inexistente. */}
        <button
          type="button"
          disabled
          aria-describedby={`batch-report-why-${b.id}`}
          className="inline-flex items-center min-h-[44px] px-[14px] rounded-lg border-[1.5px] border-nav-card bg-[#f2f5f7] font-head text-[12.5px] font-bold text-nav-faint cursor-not-allowed"
        >
          {t('f5t6c.batches.detail.report')}
        </button>
        <p className="font-body text-[12.5px] text-nav-slate m-0">
          {t('f5t6c.batches.detail.reportNote')}{' '}
          <span id={`batch-report-why-${b.id}`}>{t('f5t6c.batches.detail.reportDisabled')}</span>
        </p>
      </div>

      {/* Conservado demotado (E-23): notas, hash, breakdown de estados y
          reparto por agente del detalle viejo — vivos, no dibujados. */}
      {b.notes && (
        <div className="mt-5 bg-[#fafbfc] border-[1.5px] border-nav-card rounded-[12px] px-4 py-3.5">
          <p className="font-head text-[10.5px] font-bold tracking-[.12em] uppercase text-nav-slate m-0 mb-1">
            {t('f5t6c.batches.detail.notes')}
          </p>
          <p className="font-body text-[13px] text-nav-navy m-0 whitespace-pre-wrap break-words">{b.notes}</p>
        </div>
      )}
      <p className="mt-3 font-body text-[11px] text-nav-faint tabular-nums break-all m-0">
        SHA-256 {b.fileHashSha256}
      </p>

      {detailQ.data && (
        <div className="mt-5 grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-4">
          <div className="bg-[#fafbfc] border-[1.5px] border-nav-card rounded-[12px] px-4 py-3.5">
            <p className="font-head text-[10.5px] font-bold tracking-[.12em] uppercase text-nav-slate m-0 mb-2">
              {t('f5t6c.batches.detail.statusTitle')}
            </p>
            <ul className="m-0 p-0 list-none space-y-1">
              {detailQ.data.statusBreakdown.map((s) => (
                <li key={s.status} className="flex items-center justify-between gap-3 font-body text-[13px] text-nav-navy">
                  <span className="capitalize">{s.status.replace(/_/g, ' ')}</span>
                  <span className="font-head font-bold tabular-nums">{fmtNum(s.count)}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-[#fafbfc] border-[1.5px] border-nav-card rounded-[12px] px-4 py-3.5">
            <p className="font-head text-[10.5px] font-bold tracking-[.12em] uppercase text-nav-slate m-0 mb-2">
              {t('f5t6c.batches.detail.agentsTitle')}
            </p>
            <ul className="m-0 p-0 list-none space-y-1">
              {detailQ.data.assignmentByAgent.map((a) => (
                <li key={a.agentId} className="flex items-center justify-between gap-3 font-body text-[13px] text-nav-navy">
                  <span className="truncate">{a.fullName}</span>
                  <span className="font-head font-bold tabular-nums">{fmtNum(a.count)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailStat({
  label,
  value,
  muted,
  amber,
}: {
  label: string;
  value: string;
  muted?: boolean;
  amber?: boolean;
}): JSX.Element {
  return (
    <div>
      <p
        className={cn(
          'font-head text-[28px] font-extrabold leading-none tracking-[-0.02em] tabular-nums m-0',
          muted ? 'text-nav-faint' : amber ? 'text-nav-amberText' : 'text-nav-navy',
        )}
      >
        {value}
      </p>
      <p className={cn('font-body text-[12.5px] m-0 mt-1', amber ? 'text-nav-amberText' : 'text-nav-slate')}>
        {label}
      </p>
    </div>
  );
}

type FileReject = 'type' | 'size' | null;

function UploadModal({
  onClose,
  onUploaded,
  fmtNum,
  t,
}: {
  onClose: () => void;
  onUploaded: () => void;
  fmtNum: (n: number) => string;
  t: ReturnType<typeof useT>;
}): JSX.Element {
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<1 | 2>(1);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<BatchPreviewResponse | null>(null);
  const [reject, setReject] = useState<FileReject>(null);
  const [dragActive, setDragActive] = useState(false);
  const [meta, setMeta] = useState<UploadMetadata>({});

  const previewMut = usePreviewBatch();
  const uploadMut = useUploadBatch();
  const busy = uploadMut.isPending;

  // Escape cierra sin crear nada; mientras la TX corre, nada cierra.
  useEffect(() => {
    panelRef.current?.focus();
    const onKey = (e: globalThis.KeyboardEvent): void => {
      if (e.key === 'Escape' && !busy) onClose();
      if (e.key === 'Tab' && panelRef.current) {
        // Foco atrapado en el modal (contrato a11y).
        const focusables = panelRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (focusables.length === 0) return;
        const first = focusables[0]!;
        const last = focusables[focusables.length - 1]!;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  // Validaciones conservadas TAL CUAL (tipo y tamaño); cambia solo la cáscara:
  // el toast muere y la razón exacta se dice en el estado rojo del modal.
  const handleFile = useCallback(
    (f: File): void => {
      if (!/\.xlsx?$/i.test(f.name)) {
        setReject('type');
        setFile(null);
        setPreview(null);
        return;
      }
      if (f.size > MAX_BYTES) {
        setReject('size');
        setFile(null);
        setPreview(null);
        return;
      }
      setReject(null);
      setFile(f);
      setPreview(null);
      previewMut.reset();
      uploadMut.reset();
      // El preview corre al elegir el archivo (flujo conservado): es un
      // dry-run del server, no escribe nada.
      previewMut.mutate(f, { onSuccess: (p) => setPreview(p) });
    },
    [previewMut, uploadMut],
  );

  const onDrop = (e: DragEvent<HTMLLabelElement>): void => {
    e.preventDefault();
    setDragActive(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const clearFile = (): void => {
    setFile(null);
    setPreview(null);
    setReject(null);
    previewMut.reset();
    uploadMut.reset();
    if (inputRef.current) inputRef.current.value = '';
  };

  const newClients = preview ? preview.validRows - preview.duplicatesAcrossBatches : 0;
  const minors = preview?.stats.minorsCount ?? 0;
  const dupBlocked = !!preview?.alreadyUploaded;
  const previewFailed = !!previewMut.error;

  const onConfirm = (): void => {
    if (!file || busy) return;
    uploadMut.mutate(
      { file, metadata: meta },
      {
        onSuccess: (res) => {
          // Toast conservado del flujo previo (mismo mensaje de resultado).
          toast.success(
            t('f5t6c.batches.status.processed'),
            `${fmtNum(res.inserted)} · ${fmtNum(res.assignments.created)}`,
          );
          onUploaded();
        },
      },
    );
  };

  const inputCls =
    'w-full box-border min-h-[44px] px-3 border-[1.5px] border-nav-line rounded-lg font-body text-[14px] text-nav-navy bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal';

  const obsRows = useMemo(() => {
    if (!preview) return [];
    const rows: Array<{ id: string; name: string; meta: string; chip: 'ignored' | 'rejected' }> = [];
    for (const [i, d] of preview.duplicateSample.entries()) {
      rows.push({
        id: `dup-${i}`,
        name: d.existingClientLastNameMasked,
        meta: t('f5t6c.batches.modal.rowIgnored', { cin: d.cinLast6 }),
        chip: 'ignored',
      });
    }
    for (const [i, r] of preview.rejectedSample.entries()) {
      rows.push({
        id: `rej-${i}`,
        name: r.reason.replace(/_/g, ' '),
        meta: t('f5t6c.batches.modal.rowRejected', { row: r.sourceRowIndex, detail: r.details }),
        chip: 'rejected',
      });
    }
    return rows.slice(0, 5);
  }, [preview, t]);

  const obsTotal = preview ? preview.rejectedRows + preview.duplicatesAcrossBatches : 0;

  return (
    <>
      {/* Backdrop DENTRO de la tarjeta (radius 14 calza con ella). */}
      <div
        className="absolute inset-0 rounded-[14px] bg-nav-navy/40 z-10"
        onClick={() => {
          if (!busy) onClose();
        }}
        aria-hidden
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('f5t6c.batches.modal.title')}
        tabIndex={-1}
        className="absolute top-12 left-1/2 -translate-x-1/2 w-[600px] max-w-[calc(100%-64px)] max-h-[80vh] overflow-y-auto bg-white border-[1.5px] border-nav-card rounded-[14px] px-7 pt-[26px] pb-6 z-20 animate-fade-up motion-reduce:animate-none"
        style={{ boxShadow: '0 24px 60px rgba(18,43,68,.28)' }}
      >
        <div className="flex items-start justify-between gap-3 mb-1">
          <h2 className="font-head text-[19px] font-bold text-nav-navy m-0">
            {t('f5t6c.batches.modal.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label={t('f5t6c.batches.modal.close')}
            className="w-11 h-11 min-h-[44px] flex-none inline-flex items-center justify-center rounded-lg text-nav-slate hover:bg-nav-soft disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
          >
            <X className="h-[18px] w-[18px]" aria-hidden />
          </button>
        </div>
        <p className="font-body text-[13.5px] text-nav-slate m-0 mb-[18px]">
          {step === 1 ? t('f5t6c.batches.modal.step1') : t('f5t6c.batches.modal.step2')}
        </p>

        {step === 1 && (
          <>
            {reject || previewFailed || dupBlocked ? (
              // ARCHIVO RECHAZADO — único uso del rojo: la razón exacta y un
              // solo botón «Elegir otro archivo».
              <div className="bg-nav-redTint border-[1.5px] border-[#e3b5ae] rounded-[12px] px-5 py-[18px] mb-[18px]">
                <p className="font-head text-[14px] font-bold text-nav-red m-0">
                  {t('f5t6c.batches.modal.rejTitle')}
                </p>
                <p className="font-body text-[13.5px] text-nav-red m-0 mt-1">
                  {reject === 'type'
                    ? t('f5t6c.batches.modal.rejType')
                    : reject === 'size'
                      ? t('f5t6c.batches.modal.rejSize')
                      : dupBlocked
                        ? t('f5t6c.batches.modal.rejDup')
                        : (previewMut.error as Error | null)?.message ?? t('f5t6c.batches.modal.rejTitle')}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    clearFile();
                    inputRef.current?.click();
                  }}
                  className="mt-3 inline-flex items-center min-h-[44px] px-[18px] rounded-lg border-[1.5px] border-nav-navy bg-nav-navy font-head text-[13.5px] font-bold text-white hover:bg-nav-navyHover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2"
                >
                  {t('f5t6c.batches.modal.pickAnother')}
                </button>
              </div>
            ) : null}

            <label
              onDragOver={(e) => {
                e.preventDefault();
                setDragActive(true);
              }}
              onDragLeave={() => setDragActive(false)}
              onDrop={onDrop}
              className={cn(
                'flex flex-col items-center justify-center gap-2 min-h-[150px] rounded-[12px] border-[1.5px] border-dashed border-nav-teal bg-[#f4fafa] p-5 mb-[18px] cursor-pointer transition-colors duration-150',
                'focus-within:ring-2 focus-within:ring-nav-teal',
                dragActive && 'bg-nav-tealTint',
              )}
            >
              {/* Zona de archivo: arrastre + botón real de selección (input file accesible). */}
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="sr-only"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFile(f);
                }}
              />
              <Upload className="h-[26px] w-[26px] text-nav-teal" aria-hidden />
              {file ? (
                <>
                  {/* El archivo elegido se anuncia con nombre y tamaño. */}
                  <p className="font-head text-[14px] font-bold text-nav-navy m-0 break-all text-center" aria-live="polite">
                    {file.name}
                  </p>
                  <p className="font-body text-[12.5px] text-nav-slate tabular-nums m-0">
                    {(file.size / 1024 / 1024).toFixed(1)} MB
                  </p>
                </>
              ) : (
                <p className="font-body text-[13.5px] text-nav-slate m-0">{t('f5t6c.batches.modal.dropIdle')}</p>
              )}
            </label>

            <div className="bg-[#fafbfc] border-[1.5px] border-nav-card rounded-[12px] px-[18px] py-4 mb-[18px]">
              <p className="font-head text-[10.5px] font-bold tracking-[.12em] uppercase text-nav-slate m-0 mb-1">
                {t('f5t6c.batches.modal.limits')}
              </p>
              <p className="font-body text-[13px] leading-relaxed text-nav-navy m-0">
                {t('f5t6c.batches.modal.limitsText')}
              </p>
            </div>

            {/* Conservado (E-23 + flujo de subida intacto): metadatos opcionales
                que viajan en el payload del upload. */}
            <div className="mb-[18px]">
              <p className="font-head text-[10.5px] font-bold tracking-[.12em] uppercase text-nav-slate m-0 mb-2">
                {t('f5t6c.batches.modal.metaTitle')}
              </p>
              <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3">
                <input
                  type="text"
                  value={meta.programName ?? ''}
                  onChange={(e) => setMeta({ ...meta, programName: e.target.value })}
                  placeholder={t('f5t6c.batches.modal.metaProgram')}
                  aria-label={t('f5t6c.batches.modal.metaProgram')}
                  maxLength={100}
                  className={inputCls}
                />
                <input
                  type="date"
                  value={meta.sourceDate ?? ''}
                  onChange={(e) => setMeta({ ...meta, sourceDate: e.target.value })}
                  aria-label={t('f5t6c.batches.modal.metaDate')}
                  className={inputCls}
                />
              </div>
              <textarea
                value={meta.notes ?? ''}
                onChange={(e) => setMeta({ ...meta, notes: e.target.value })}
                placeholder={t('f5t6c.batches.modal.metaNotes')}
                aria-label={t('f5t6c.batches.modal.metaNotes')}
                maxLength={2000}
                rows={2}
                className={cn(inputCls, 'mt-3 py-2.5 resize-y')}
              />
            </div>

            <div className="flex justify-end gap-2.5">
              <button
                type="button"
                onClick={onClose}
                className="min-h-[44px] px-[18px] border-[1.5px] border-nav-line rounded-lg bg-white font-head text-[13.5px] font-bold text-nav-navy hover:bg-[#f2f5f7] hover:border-nav-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
              >
                {t('f5t6c.batches.modal.cancel')}
              </button>
              <button
                type="button"
                onClick={() => setStep(2)}
                disabled={!preview || dupBlocked || previewMut.isPending}
                className="min-h-[44px] px-5 bg-nav-navy text-white border-[1.5px] border-nav-navy rounded-lg font-head text-[13.5px] font-bold hover:bg-nav-navyHover disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2"
              >
                {previewMut.isPending ? t('f5t6c.batches.modal.reading') : t('f5t6c.batches.modal.preview')}
              </button>
            </div>
          </>
        )}

        {step === 2 && preview && (
          <>
            <span className="inline-flex items-center gap-1.5 rounded-full border-[1.5px] border-nav-teal bg-nav-tealTint text-nav-tealDark px-3.5 py-[7px] font-head text-[12px] font-bold">
              <Lock className="h-[15px] w-[15px]" aria-hidden />
              {t('f5t6c.batches.modal.readonly')}
            </span>

            {newClients === 0 ? (
              // PREVIEW CON CERO CREABLES — confirmar no haría nada.
              <div className="mt-[18px]">
                <p className="font-head text-[16px] font-bold text-nav-navy m-0">
                  {t('f5t6c.batches.modal.zero', { n: fmtNum(preview.totalRows) })}
                </p>
                <p className="font-body text-[13.5px] text-nav-slate m-0 mt-1">
                  {t('f5t6c.batches.modal.zeroBody')}
                </p>
                <div className="flex justify-end mt-5">
                  <button
                    type="button"
                    onClick={onClose}
                    className="min-h-[44px] px-[18px] border-[1.5px] border-nav-line rounded-lg bg-white font-head text-[13.5px] font-bold text-nav-navy hover:bg-[#f2f5f7] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
                  >
                    {t('f5t6c.batches.modal.close')}
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Las 4 cifras se anuncian JUNTAS por aria-live. */}
                <p aria-live="polite" role="status" className="sr-only">
                  {t('f5t6c.batches.aria.figures', {
                    create: newClients,
                    ignored: preview.duplicatesAcrossBatches,
                    rejected: preview.rejectedRows,
                    minors,
                  })}
                </p>
                <div className="grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-4 mt-[18px] mb-5">
                  <DetailStat label={t('f5t6c.batches.modal.stat.create')} value={fmtNum(newClients)} />
                  <DetailStat label={t('f5t6c.batches.modal.stat.ignored')} value={fmtNum(preview.duplicatesAcrossBatches)} muted />
                  <DetailStat label={t('f5t6c.batches.modal.stat.rejected')} value={fmtNum(preview.rejectedRows)} amber />
                  <DetailStat label={t('f5t6c.batches.modal.stat.minors')} value={fmtNum(minors)} amber />
                </div>

                {minors > 0 && (
                  <div className="flex items-start gap-2.5 bg-nav-amberTint border-[1.5px] border-nav-amber rounded-[12px] px-[18px] py-4 text-nav-amberText mb-[18px]">
                    <AlertTriangle className="h-[17px] w-[17px] flex-none mt-px" aria-hidden />
                    <p className="font-body text-[13.5px] leading-[1.55] m-0">
                      {t('f5t6c.batches.modal.minors', { n: fmtNum(minors) })}
                    </p>
                  </div>
                )}

                {obsRows.length > 0 && (
                  <>
                    <div className="border-[1.5px] border-nav-card rounded-[10px] overflow-hidden mt-[18px]">
                      {/* NECESITA-SERVER: las filas de MENORES una a una — el
                          server manda solo el conteo (stats.minorsCount). */}
                      {obsRows.map((r, i) => (
                        <div
                          key={r.id}
                          className={cn(
                            'flex items-center justify-between gap-3 min-h-[56px] px-3.5 py-[9px] bg-white',
                            i > 0 && 'border-t border-nav-hair',
                          )}
                        >
                          <span className="min-w-0">
                            <span className="block font-head text-[13px] font-bold text-nav-navy truncate">
                              {r.name || '—'}
                            </span>
                            <span className="block font-body text-[12px] text-nav-slate tabular-nums truncate">
                              {r.meta}
                            </span>
                          </span>
                          <span
                            className={cn(
                              'flex-none inline-flex items-center rounded-full border px-2.5 py-[3px] font-head text-[11px] font-bold',
                              r.chip === 'ignored'
                                ? 'bg-nav-hair text-nav-slate border-nav-card'
                                : 'bg-nav-amberTint text-nav-amberText border-[#e6cf9a]',
                            )}
                          >
                            {r.chip === 'ignored'
                              ? t('f5t6c.batches.modal.chip.ignored')
                              : t('f5t6c.batches.modal.chip.rejected')}
                          </span>
                        </div>
                      ))}
                    </div>
                    <p className="font-body text-[12.5px] text-nav-slate mt-2 m-0">
                      {t('f5t6c.batches.modal.obsNote', { shown: obsRows.length, total: fmtNum(obsTotal) })}
                    </p>
                  </>
                )}

                {/* Conservado demotado (E-23): avisos del parser + demografía. */}
                {(
                  <details className="mt-4">
                    <summary className="cursor-pointer font-head text-[12px] font-bold text-nav-tealDark min-h-[44px] inline-flex items-center px-1">
                      {t('f5t6c.batches.modal.warnTitle')} · {t('f5t6c.batches.modal.demoTitle')}
                    </summary>
                    <div className="mt-2 space-y-2">
                      {preview.warnings.map((w, i) => (
                        <p key={i} className="font-body text-[12.5px] text-nav-slate m-0">
                          {w.message}
                          {w.examples?.length ? ` · ${w.examples.join(', ')}` : ''}
                        </p>
                      ))}
                      <p className="font-body text-[12.5px] text-nav-slate tabular-nums m-0">
                        {`${preview.stats.ageMin}–${preview.stats.ageMax} · ${preview.stats.ageAvg.toFixed(1)} · <18: ${preview.stats.minorsCount} · 65+: ${preview.stats.elderlyCount} · Bronx: ${preview.stats.bronxCount} / ${preview.stats.nonBronxCount}`}
                      </p>
                    </div>
                  </details>
                )}

                {uploadMut.error && (
                  <p role="alert" className="font-body text-[13px] text-nav-red mt-3 m-0">
                    {(uploadMut.error as Error).message}
                  </p>
                )}

                <div className="flex justify-end gap-2.5 mt-5">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    disabled={busy}
                    className="min-h-[44px] px-[18px] border-[1.5px] border-nav-line rounded-lg bg-white font-head text-[13.5px] font-bold text-nav-navy hover:bg-[#f2f5f7] hover:border-nav-faint disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
                  >
                    {t('f5t6c.batches.modal.back')}
                  </button>
                  {/* El botón final dice la CANTIDAD EXACTA, nunca «Confirmar». */}
                  <button
                    type="button"
                    onClick={onConfirm}
                    disabled={busy}
                    className="min-h-[44px] px-5 bg-nav-navy text-white border-[1.5px] border-nav-navy rounded-lg font-head text-[13.5px] font-bold hover:bg-nav-navyHover disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2"
                  >
                    {busy
                      ? t('f5t6c.batches.modal.creating')
                      : t('f5t6c.batches.modal.create', { n: fmtNum(newClients) })}
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </>
  );
}
